import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate, type Identifier } from "ra-core";

import { StatusPill } from "../design/StatusPill";
import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppLeadSignal } from "../types";
import { UNREAD_CAP } from "./whatsappInboxRules";

const SIGNALS_KEY = ["whatsapp", "leadSignals"];
const SIGNALS_STALE_MS = 60_000;

/**
 * The WhatsApp signal of one lead (alert badges, 'responde', unread count), or `null`. All the
 * lead rows of a screen share ONE cached request (RLS decides which leads come back), so adding
 * a badge to a card costs no extra call. A failing read renders nothing: the badges are a
 * convenience, never a gate.
 */
export const useWhatsAppLeadSignal = (
  dealId: Identifier | null | undefined,
): WhatsAppLeadSignal | null => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const signals = useQuery({
    queryKey: SIGNALS_KEY,
    queryFn: async () =>
      new Map(
        (await dataProvider.getWhatsAppLeadSignals()).map((signal) => [
          String(signal.dealId),
          signal,
        ]),
      ),
    staleTime: SIGNALS_STALE_MS,
    retry: false,
    enabled: dealId != null,
  });
  return dealId == null ? null : (signals.data?.get(String(dealId)) ?? null);
};

/**
 * Almikia Epic EW2 (TASK-080): lead-row badges. Red pills when the 24 h 'Escribir libre' clock
 * has under 2 h left or the 7-day 'Mensajes gratis' clock under 1 day (thresholds are
 * `whatsapp_config` data, decided by the server view), plus the 'Responde' badge and the unread
 * count. Renders nothing for a lead without a signal. No call buttons, no phone numbers.
 */
export const LeadWhatsAppBadges = ({
  dealId,
  className,
}: {
  dealId: Identifier | null | undefined;
  className?: string;
}) => {
  const translate = useTranslate();
  const signal = useWhatsAppLeadSignal(dealId);
  if (
    !signal ||
    !(
      signal.alert24h ||
      signal.alertCtwa ||
      signal.awaitingReply ||
      signal.unreadCount > 0
    )
  ) {
    return null;
  }
  const unread =
    signal.unreadCount > UNREAD_CAP ? `${UNREAD_CAP}+` : signal.unreadCount;
  return (
    <div
      data-testid="lead-whatsapp-badges"
      className={`flex flex-wrap gap-2 ${className ?? ""}`}
    >
      {signal.alert24h ? (
        <StatusPill
          tone="not-ok"
          text={translate("crm.whatsapp.badges.alert_24h")}
        />
      ) : null}
      {signal.alertCtwa ? (
        <StatusPill
          tone="not-ok"
          text={translate("crm.whatsapp.badges.alert_ctwa")}
        />
      ) : null}
      {signal.awaitingReply ? (
        <StatusPill
          tone="ok"
          text={translate("crm.whatsapp.badges.responde")}
        />
      ) : null}
      {signal.unreadCount > 0 ? (
        <span className="text-sm text-muted-foreground tabular-nums">
          {translate("crm.whatsapp.badges.unread", {
            count: unread,
          })}
        </span>
      ) : null}
    </div>
  );
};
